import classNames from 'classnames';
import { InstrumentPanel } from 'lib-analytics-types';
import mapValues from 'lodash/mapValues';
import { memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import {
  ItemCallback,
  Layout,
  Layouts,
  Responsive as ResponsiveGridLayout,
} from 'react-grid-layout';
import { useMeasure } from 'react-use';

import {
  defaultBreakpoints,
  defaultBreakpointsDetails,
} from 'utils/mediaQuery/defaultConfig';
import { useDelayedState } from 'utils/useDelayedState';

import styles from './analyticsPanels.module.scss';
import { AnalyticsExportModal } from './components/export/components/modal/analyticsExportModal';
import {
  AnalyticsPanel,
  AnalyticsPanelProps,
} from './components/panel/analyticsPanel';
import { InstrumentPanelResizeHandle } from './components/panel/components/panel/components/resizeHandle/instrumentPanelResizeHandle';
import { panelGridLayoutConfig } from './utils/panelGridLayoutConfig';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

type AnalyticsPanelsProps = {
  /** The list of instrument panels to render. */
  panels: readonly InstrumentPanel[];
  /** Whether the panels layout can be customized by the user. */
  isCustomizable?: boolean;
  /** Callback for when the panels layout changes. */
  onChangeLayout?: (layout: Layout[], event: 'drag' | 'resize') => void;
} & Pick<
  AnalyticsPanelProps,
  'onPin' | 'onUnpin' | 'onChangeControls' | 'disabledControls'
>;

export const AnalyticsPanels = memo(function AnalyticsPanels({
  panels,
  isCustomizable = false,
  onChangeLayout,
  ...panelProps
}: AnalyticsPanelsProps): JSX.Element {
  const [gridRef, { width }] = useMeasure<HTMLDivElement>();
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isAnimated, setIsAnimated] = useDelayedState(false, 0);

  // By default, react-grid-layout component animates the layout on mount. We disable that by
  // setting `transition-property: none` on initial render using the `gridAnimated` class, and then
  // we enable it again immediately after the first render in the next event loop tick.
  useLayoutEffect(() => {
    setIsAnimated(true);
  });

  /**
   * The breakpoints object consisting of a breakpoint key and the screen width
   * where the breakpoint takes effect. The key names are arbitrary and they're
   * derived from the global breakpoint definitions, but they must match the
   * ones used in the `cols` and `layouts` values below for the grid to work.
   */
  const breakpoints = useMemo(
    () =>
      defaultBreakpointsDetails.breakpoints.reduce(
        (acc, { breakpoint, minWidth }) => ({
          ...acc,
          [breakpoint]: parseInt(minWidth, 10),
        }),
        {} as Record<keyof typeof defaultBreakpoints, number>,
      ),
    [],
  );

  /**
   * The number of grid columns for each breakpoint key. For now, we don't
   * change the number of columns based on screen width.
   */
  const cols = useMemo(
    () => mapValues(breakpoints, () => panelGridLayoutConfig.cols),
    [breakpoints],
  );

  /**
   * The responsive layouts configurations. By default, we use the panel
   * `layout` defined in the API configuration. Where necessary, we override
   * those layouts according to what works best for each breakpoint.
   */
  const layouts: Layouts = useMemo(() => {
    const defaultLayouts: Layout[] = panels.map((panel) => ({
      i: panel.id,
      ...panel.layout,
    }));

    const responsiveLayouts = mapValues(breakpoints, (_value, key) => {
      switch (key) {
        // On smaller screens, we stack the panels vertically. Each panel gets a width of 100% and
        // the Y position is calculated based on the previous panel's height and Y position.
        case 'base':
        case 'sm':
          return defaultLayouts.map((layout, i) => ({
            ...layout,
            w: panelGridLayoutConfig.cols,
            y:
              i > 0
                ? defaultLayouts[i - 1].h + defaultLayouts[i - 1].y
                : layout.y,
          }));

        default:
          return defaultLayouts;
      }
    });

    return responsiveLayouts;
  }, [breakpoints, panels]);

  const handleDragResizeStart: ItemCallback = useCallback(() => {
    setIsCustomizing(true);
  }, []);

  const handleDragStop: ItemCallback = useCallback(
    (layout) => {
      setIsCustomizing(false);
      onChangeLayout?.(layout, 'drag');
    },
    [onChangeLayout],
  );

  const handleResizeStop: ItemCallback = useCallback(
    (layout) => {
      setIsCustomizing(false);
      onChangeLayout?.(layout, 'resize');
    },
    [onChangeLayout],
  );

  /**
   * Whether the panels originate from the same or different dashboards, in
   * which case some functionality may differ.
   */
  const isMixedMode = useMemo(
    () => new Set(panels.map((panel) => panel.dashboard_id)).size > 1,
    [panels],
  );

  return (
    <>
      <div ref={gridRef}>
        <ResponsiveGridLayout
          width={width}
          className={classNames({
            [styles.grid]: true,
            [styles.gridAnimated]: isAnimated,
            [styles.gridActive]: isCustomizing,
          })}
          layouts={layouts}
          breakpoints={breakpoints}
          cols={cols}
          margin={panelGridLayoutConfig.margin}
          containerPadding={panelGridLayoutConfig.containerPadding}
          rowHeight={panelGridLayoutConfig.rowHeight}
          isDraggable={isCustomizable}
          isResizable={isCustomizable}
          draggableHandle={`.${panelGridLayoutConfig.draggableHandle}`}
          resizeHandle={isCustomizable ? <InstrumentPanelResizeHandle /> : null}
          {...(isCustomizable && {
            onDragStart: handleDragResizeStart,
            onResizeStart: handleDragResizeStart,
            onDragStop: handleDragStop,
            onResizeStop: handleResizeStop,
          })}
        >
          {panels.map((panel) => (
            <div key={panel.id}>
              <AnalyticsPanel
                panel={panel}
                isCustomizable={isCustomizable}
                isMixedMode={isMixedMode}
                {...panelProps}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>

      <AnalyticsExportModal />
    </>
  );
});
